import { Component, PLATFORM_ID, Inject, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { HomeGraficoMensalResponse } from '../../interfaces/home-grafico-mensal.interface';

@Component({
  selector: 'app-vertical-bar-chart',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule],
  templateUrl: './vertical-bar-chart.html',
  styleUrls: ['./vertical-bar-chart.scss']
})
export class VerticalBarChart implements OnChanges {
  @Input() data: HomeGraficoMensalResponse | null = null;
  @Input() loading: boolean = false;

  chartOption: EChartsOption;
  isBrowser: boolean;
  showChart = true;
  private currentIsSmallScreen = false;
  private currentIsMobile = false;
  private currentIsVerySmallScreen = false;
  private chartInstance: any;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.currentIsSmallScreen = this.getIsSmallScreen();
    this.currentIsMobile = this.getIsMobile();
    this.currentIsVerySmallScreen = this.getIsVerySmallScreen();
    this.chartOption = this.getChartOptions(this.currentIsSmallScreen, this.currentIsMobile, this.currentIsVerySmallScreen, null);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && this.isBrowser) {
      this.chartOption = this.getChartOptions(this.currentIsSmallScreen, this.currentIsMobile, this.currentIsVerySmallScreen, this.data);
      if (this.chartInstance) {
        setTimeout(() => {
          this.chartInstance.setOption(this.chartOption);
        }, 100);
      }
    }
  }

  onChartInit(ec: any) {
    this.chartInstance = ec;
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      const newIsSmallScreen = this.getIsSmallScreen();
      const newIsMobile = this.getIsMobile();
      const newIsVerySmallScreen = this.getIsVerySmallScreen();

      if (newIsSmallScreen !== this.currentIsSmallScreen || 
          newIsMobile !== this.currentIsMobile ||
          newIsVerySmallScreen !== this.currentIsVerySmallScreen) {
        this.currentIsSmallScreen = newIsSmallScreen;
        this.currentIsMobile = newIsMobile;
        this.currentIsVerySmallScreen = newIsVerySmallScreen;

        this.showChart = false;
        setTimeout(() => {
          this.chartOption = this.getChartOptions(this.currentIsSmallScreen, this.currentIsMobile, this.currentIsVerySmallScreen, this.data);
          this.showChart = true;

          setTimeout(() => {
            if (this.chartInstance) {
              this.chartInstance.resize();
            }
          }, 100);
        }, 50);
      } else {
        if (this.chartInstance) {
          this.chartInstance.resize();
        }
      }
    }
  }

  private getIsSmallScreen(): boolean {
    return this.isBrowser && window.innerWidth <= 1600;
  }

  private getIsMobile(): boolean {
    return this.isBrowser && window.innerWidth <= 768;
  }

  private getIsVerySmallScreen(): boolean {
    return this.isBrowser && window.innerWidth <= 380;
  }

  private getChartOptions(isSmallScreen: boolean, isMobile: boolean, isVerySmallScreen: boolean, apiData: HomeGraficoMensalResponse | null): EChartsOption {
    let meses: string[] = [];
    let disponibilizados: number[] = [];
    let trabalhados: number[] = [];
    let convertidos: number[] = [];
    let percentualTrabalhados: number[] = [];

    if (apiData && apiData.length > 0) {
      const sortedData = [...apiData].sort((a, b) => {
        const [mesA, anoA] = a.mesReferencia.split('/').map(Number);
        const [mesB, anoB] = b.mesReferencia.split('/').map(Number);
        if (anoA !== anoB) return anoA - anoB;
        return mesA - mesB;
      });

      meses = sortedData.map(item => {
        const [mes, ano] = item.mesReferencia.split('/');
        const mesesAbrev = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        const mesNum = parseInt(mes) - 1;
        const anoAbrev = ano.substring(2);
        return `${mesesAbrev[mesNum]}/${anoAbrev}`;
      });

      disponibilizados = sortedData.map(item => item.disponibilizados);
      trabalhados = sortedData.map(item => item.trabalhados);
      convertidos = sortedData.map(item => item.percentualConvertido);
      percentualTrabalhados = sortedData.map(item => item.percentualTrabalhados);
    } else {
      meses = [];
      disponibilizados = [];
      trabalhados = [];
      convertidos = [];
      percentualTrabalhados = [];
    }

    // Ajustes específicos para telas muito pequenas
    const legendBottom = isVerySmallScreen ? 30 : (isMobile ? 8 : 2);
    const gridBottom = isVerySmallScreen ? '28%' : (isMobile ? '28%' : (isSmallScreen ? '15%' : '18%'));
    const titleFontSize = isVerySmallScreen ? 14 : (isSmallScreen ? 16 : 20);
    const legendFontSize = isVerySmallScreen ? 14 : (isMobile ? 14 : (isSmallScreen ? 9 : 10));
    const axisFontSize = isVerySmallScreen ? 10 : (isMobile ? 12 : (isSmallScreen ? 10 : 11));
    const barWidth = isVerySmallScreen ? '18px' : (isSmallScreen ? '20px' : '25px');
    const symbolSize = isVerySmallScreen ? 6 : (isSmallScreen ? 8 : 10);
    const gridTop = isVerySmallScreen ? '15%' : (isSmallScreen ? '12%' : '15%');

    return {
      title: {
        text: 'Performance mensal',
        left: 'left',
        top: 0,
        textStyle: {
          color: '#001E50',
          fontWeight: 'bold',
          fontSize: titleFontSize
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          const month = items[0]?.axisValue || '';

          let tooltipHtml = `<div style="font-weight: bold; margin-bottom: 8px;">${month}</div>`;

          items.forEach(p => {
            const isPercentual =
              p.seriesName === 'Taxa de Conversão' ||
              p.seriesName === 'Taxa de Leads Trabalhados';

            const value = isPercentual ? Math.round(p.value) + '%' : p.value.toLocaleString('pt-BR');

            let markerColor = '#001E50';
            let markerType = 'square';

            switch(p.seriesName) {
              case 'Leads Recebidos':
                markerColor = '#00B0F0';
                markerType = 'square';
                break;
              case 'Leads Trabalhados':
                markerColor = '#006790';
                markerType = 'square';
                break;
              case 'Taxa de Conversão':
                markerColor = '#6A767D';
                markerType = 'circle';
                break;
              case 'Taxa de Leads Trabalhados':
                markerColor = '#00B0F0';
                markerType = 'circle';
                break;
            }

            let markerHtml = '';
            if (markerType === 'square') {
              markerHtml = `<span style="display: inline-block; width: 12px; height: 12px; background-color: ${markerColor}; border-radius: 2px; margin-right: 6px; vertical-align: middle;"></span>`;
            } else {
              markerHtml = `<span style="display: inline-block; width: 12px; height: 12px; border: 2px solid ${markerColor}; background-color: white; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>`;
            }

            if (p.seriesName === 'Taxa de Leads Trabalhados') {
              markerHtml = `<span style="display: inline-block; width: 12px; height: 12px; border: 2px solid ${markerColor}; background-color: white; border-radius: 50%; margin-right: 6px; vertical-align: middle;"></span>`;
            }

            tooltipHtml += `
              <div style="margin-bottom: 4px;">
                ${markerHtml}
                <span style="color: #666; font-size: 12px;">${p.seriesName}:</span>
                <span style="font-weight: bold; margin-left: 4px; color: #001E50; font-size: 12px;">${value}</span>
              </div>
            `;
          });

          return tooltipHtml;
        }
      },
      legend: {
        data: ['Leads Recebidos', 'Leads Trabalhados', 'Taxa de Leads Trabalhados', 'Taxa de Conversão'],
        itemHeight: isVerySmallScreen ? 8 : (isSmallScreen ? 8 : 10),
        itemWidth: isVerySmallScreen ? 8 : (isSmallScreen ? 8 : 10),
        bottom: legendBottom,
        textStyle: {
          fontSize: legendFontSize,
          color: '#000E26'
        }
      },
      grid: {
        left: '3%',
        right: '8%',
        top: gridTop,
        bottom: gridBottom,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: meses,
        axisLabel: {
          color: '#6A767D',
          fontSize: axisFontSize,
          rotate: isVerySmallScreen ? 30 : 0
        },
        axisLine: {
          lineStyle: {
            color: '#E5E7EB'
          }
        },
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: [
        {
          type: 'value',
          position: 'left',
          min: 0,
          max: 100,
          axisLabel: {
            color: '#6A767D',
            fontSize: isVerySmallScreen ? 9 : (isSmallScreen ? 10 : 11),
            formatter: '{value}%'
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#E5E7EB'
            }
          },
          axisTick: {
            show: true
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#F3F4F6',
              type: 'dashed'
            }
          },
          nameTextStyle: {
            fontSize: isVerySmallScreen ? 9 : (isSmallScreen ? 10 : 11),
            color: '#6A767D'
          }
        },
        {
          type: 'value',
          position: 'right',
          axisLabel: {
            color: '#6A767D',
            fontSize: isVerySmallScreen ? 9 : (isSmallScreen ? 10 : 11)
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#F3F4F6',
              type: 'dashed'
            }
          },
          nameTextStyle: {
            fontSize: isVerySmallScreen ? 9 : (isSmallScreen ? 10 : 11),
            color: '#6A767D'
          }
        }
      ],
      series: [
        {
          name: 'Leads Recebidos',
          type: 'bar',
          data: disponibilizados,
          barWidth: barWidth,
          yAxisIndex: 1,
          itemStyle: {
            color: '#00B0F0',
            borderRadius: [3, 3, 0, 0]
          }
        },
        {
          name: 'Leads Trabalhados',
          type: 'bar',
          data: trabalhados,
          barWidth: barWidth,
          yAxisIndex: 1,
          itemStyle: {
            color: '#006790',
            borderRadius: [3, 3, 0, 0]
          }
        },
        {
          name: 'Taxa de Leads Trabalhados',
          type: 'line',
          data: percentualTrabalhados,
          symbol: 'circle',
          symbolSize: symbolSize,
          yAxisIndex: 0,
          lineStyle: {
            color: '#00B0F0',
            width: isVerySmallScreen ? 1.5 : 2,
            type: 'dashed'
          },
          itemStyle: {
            color: '#FFFFFF',
            borderColor: '#00B0F0',
            borderWidth: isVerySmallScreen ? 1.5 : 2
          }
        },
        {
          name: 'Taxa de Conversão',
          type: 'line',
          data: convertidos,
          symbol: 'circle',
          symbolSize: symbolSize,
          yAxisIndex: 0,
          lineStyle: {
            color: '#6A767D',
            width: isVerySmallScreen ? 1.5 : 2
          },
          itemStyle: {
            color: '#FFFFFF',
            borderColor: '#6A767D',
            borderWidth: isVerySmallScreen ? 1.5 : 2
          }
        }
      ]
    };
  }
}